import { cn, FormBadge, FormPrevalenceBadge, FormScoringBadge, ui } from "@patient-watch/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import type { FormViewT } from "@patient-watch/types";
import { Link, useNavigate } from "@tanstack/react-router";
import {
    type ColumnDef,
    type ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, MoreHorizontal, SlidersHorizontal } from "lucide-react";
import * as React from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import MultiSelect from "./MultiSelect";
import { unknownToDisplayString } from "./utils/unknownToDisplayString";

const DEFAULT_WEBSITE_URL = "https://patient.watch";

// Custom sorting function that handles null values
const stringSortFn = (rowA: any, rowB: any, columnId: string) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);
    const aValue = a == null ? "" : String(a);
    const bValue = b == null ? "" : String(b);
    return aValue.localeCompare(bValue);
};

function FormRowActions({ form, websiteUrl }: { form: FormViewT; websiteUrl: string }) {
    const formId = form.form_id || "";
    const slug = typeof form.slug === "string" && form.slug.trim() ? form.slug.trim() : null;
    const detailsHref = slug ? `${websiteUrl}/forms/${slug}` : null;

    return (
        <ui.DropdownMenu>
            <ui.DropdownMenuTrigger asChild>
                <ui.Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 shrink-0"
                    aria-label="Form actions"
                    onClick={(event: React.MouseEvent) => event.stopPropagation()}
                >
                    <MoreHorizontal className="w-4 h-4" />
                </ui.Button>
            </ui.DropdownMenuTrigger>
            <ui.DropdownMenuContent
                align="end"
                className="w-44"
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
                {detailsHref ? (
                    <ui.DropdownMenuItem asChild>
                        <a
                            href={detailsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Details
                        </a>
                    </ui.DropdownMenuItem>
                ) : null}
                <ui.DropdownMenuItem asChild>
                    <Link
                        to="/forms/$formId"
                        params={{ formId }}
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={(event) => event.stopPropagation()}
                    >
                        Open
                    </Link>
                </ui.DropdownMenuItem>
            </ui.DropdownMenuContent>
        </ui.DropdownMenu>
    );
}

const columns: ColumnDef<FormViewT>[] = [
    {
        accessorKey: "form_name",
        header: "Name",
        cell: (info) => {
            const name = unknownToDisplayString(info.getValue(), "");
            return (
                <span className="block truncate" title={name || undefined}>
                    {name || "-"}
                </span>
            );
        },
        enableSorting: true,
        sortingFn: stringSortFn,
    },
    {
        id: "number_of_pages",
        header: "Pages",
        accessorFn: (row) => row.number_of_pages ?? (Array.isArray(row.pages) ? row.pages.length : 0),
        enableSorting: true,
        cell: (info) => info.getValue() || "-",
    },
    {
        id: "number_of_questions",
        header: "Questions",
        accessorFn: (row) =>
            row.number_of_questions ??
            (Array.isArray(row.pages)
                ? (row.pages as unknown[]).reduce(
                      (acc: number, page) =>
                          Array.isArray((page as { inputs?: unknown[] })?.inputs)
                              ? acc + ((page as { inputs?: unknown[] })?.inputs?.length ?? 0)
                              : acc,
                      0
                  )
                : 0),
        enableSorting: true,
        cell: (info) => info.getValue() || "-",
    },
    {
        accessorKey: "orthopaedic_areas",
        header: "Anatomical Areas",
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) =>
            Array.isArray(info.getValue()) ? (
                <div className="flex flex-nowrap gap-1 overflow-hidden">
                    {(info.getValue() as string[]).map((area) => (
                        <FormBadge key={area} variant="anatomic">
                            {area}
                        </FormBadge>
                    ))}
                </div>
            ) : null,
        filterFn: (row, columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return true;
            }
            const value = row.getValue(columnId);
            if (Array.isArray(value)) {
                return value.some((v) => filterValue.includes(v));
            }
            return false;
        },
        sortingFn: (rowA, rowB) => {
            const a = rowA.getValue("orthopaedic_areas");
            const b = rowB.getValue("orthopaedic_areas");
            const aValue = Array.isArray(a) && a.length > 0 ? a[0] : "";
            const bValue = Array.isArray(b) && b.length > 0 ? b[0] : "";
            return String(aValue).localeCompare(String(bValue));
        },
    },
    {
        accessorKey: "prevalence",
        header: "Prevalence",
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => <FormPrevalenceBadge prevalence={info.getValue() as string} />,
        filterFn: (row, columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return true;
            }
            const value = row.getValue(columnId);
            return filterValue.includes(value);
        },
        sortingFn: stringSortFn,
    },
    {
        accessorKey: "scoring",
        header: "Scoring",
        enableSorting: true,
        cell: (info) => <FormScoringBadge scoring={info.row.original.scoring} />,
        sortingFn: stringSortFn,
    },
    {
        accessorKey: "time_to_complete",
        header: "Time to Complete",
        enableSorting: true,
        cell: (info) => {
            const v = info.getValue();
            return v != null && v !== "" ? `${unknownToDisplayString(v, "")} min` : "-";
        },
    },
];

function getFormsTableColumns(websiteUrl: string): ColumnDef<FormViewT>[] {
    return [
        ...columns,
        {
            id: "actions",
            header: () => <span className="sr-only">Actions</span>,
            cell: ({ row }) => <FormRowActions form={row.original} websiteUrl={websiteUrl} />,
            enableSorting: false,
        },
    ];
}

function useFormsTable(forms: FormViewT[], websiteUrl: string) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    // For filter UI
    const allPrevalence = React.useMemo(() => {
        const set = new Set<string>();
        for (const form of forms) {
            if (typeof (form as { prevalence?: string }).prevalence === "string") {
                set.add((form as { prevalence: string }).prevalence);
            }
        }
        return Array.from(set);
    }, [forms]);
    const allAreas = React.useMemo(() => {
        const set = new Set<string>();
        for (const form of forms) {
            if (Array.isArray((form as { orthopaedic_areas?: string[] }).orthopaedic_areas)) {
                for (const area of (form as { orthopaedic_areas: string[] }).orthopaedic_areas) {
                    if (typeof area === "string") set.add(area);
                }
            }
        }
        return Array.from(set);
    }, [forms]);

    const tableColumns = React.useMemo(() => getFormsTableColumns(websiteUrl), [websiteUrl]);

    const table = useReactTable({
        data: forms,
        columns: tableColumns,
        state: { sorting, columnFilters },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        debugTable: false,
    });

    return {
        table,
        tableColumns,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        allPrevalence,
        allAreas,
    };
}

interface FilterFormT {
    prevalence: string[];
    orthopaedic_areas: string[];
}

function FormsTableFilterPopover({
    allPrevalence,
    allAreas,
    onSave,
    initialPrevalence,
    initialAreas,
}: {
    allPrevalence: string[];
    allAreas: string[];
    onSave: (filters: FilterFormT) => void;
    initialPrevalence: string[];
    initialAreas: string[];
}) {
    const form = useForm<FilterFormT>({
        defaultValues: {
            prevalence: initialPrevalence,
            orthopaedic_areas: initialAreas,
        },
    });
    const handleSubmit: SubmitHandler<FilterFormT> = (data) => {
        onSave(data);
    };
    return (
        <ui.Popover>
            <ui.Button asChild variant="outline" size="sm">
                <ui.PopoverTrigger>
                    <SlidersHorizontal size={20} />
                    <span className="sr-only md:not-sr-only">Filter</span>
                </ui.PopoverTrigger>
            </ui.Button>
            <ui.PopoverContent align="end" className="flex flex-col gap-2">
                <h3 className="text-md text-slate-900 dark:text-slate-100">Filter Forms</h3>
                <ui.Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-y-0 space-y-2">
                        <div className="space-y-1">
                            <ui.Label>Prevalence</ui.Label>

                            <MultiSelect
                                name="prevalence"
                                options={allPrevalence.map((p) => ({
                                    value: p,
                                    label: p,
                                }))}
                                trigger="Select Prevalence"
                            />
                        </div>
                        <div className="space-y-1">
                            <ui.Label>Anatomical Areas</ui.Label>
                            <MultiSelect
                                name="orthopaedic_areas"
                                options={allAreas.map((a) => ({
                                    value: a,
                                    label: a,
                                }))}
                                trigger="Select Areas"
                            />
                        </div>
                        <ui.Button type="submit" size="sm">
                            Save
                        </ui.Button>
                    </form>
                </ui.Form>
            </ui.PopoverContent>
        </ui.Popover>
    );
}

export function FormsTable({ forms, websiteUrl = DEFAULT_WEBSITE_URL }: { forms: FormViewT[]; websiteUrl?: string }) {
    const navigate = useNavigate();
    const resolvedWebsiteUrl = websiteUrl.replace(/\/$/, "") || DEFAULT_WEBSITE_URL;
    const { table, tableColumns, allPrevalence, allAreas } = useFormsTable(forms, resolvedWebsiteUrl);

    // Filter UI for prevalence
    const prevalenceFilter = table.getColumn("prevalence");
    // Filter UI for orthopaedic_areas
    const areasFilter = table.getColumn("orthopaedic_areas");

    const [filterState, setFilterState] = React.useState<FilterFormT>({
        prevalence: [],
        orthopaedic_areas: [],
    });

    // Update table filters when filterState changes
    React.useEffect(() => {
        if (prevalenceFilter) {
            prevalenceFilter.setFilterValue(filterState.prevalence.length ? filterState.prevalence : undefined);
        }
        if (areasFilter) {
            areasFilter.setFilterValue(
                filterState.orthopaedic_areas.length ? filterState.orthopaedic_areas : undefined
            );
        }
    }, [filterState, prevalenceFilter, areasFilter]);

    return (
        <>
            <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-4 mb-2">
                <h2 className="font-bold text-2xl tracking-tight">All Forms</h2>
                <FormsTableFilterPopover
                    allPrevalence={allPrevalence}
                    allAreas={allAreas}
                    initialPrevalence={filterState.prevalence}
                    initialAreas={filterState.orthopaedic_areas}
                    onSave={setFilterState}
                />
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-lg overflow-hidden">
                <Table className="w-full table-fixed">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="top-0 z-10 sticky bg-slate-50 dark:bg-slate-800">
                                {headerGroup.headers.map((header) => {
                                    const canSort = header.column.getCanSort();
                                    const isName = header.column.id === "form_name";
                                    const isActions = header.column.id === "actions";
                                    const sorted = header.column.getIsSorted();
                                    const SortIcon =
                                        sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                "group",
                                                canSort && "cursor-pointer select-none",
                                                isName && "w-[40%]",
                                                isActions && "w-12 pr-2"
                                            )}
                                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                        >
                                            <span
                                                className={cn(
                                                    "inline-flex items-center gap-1 max-w-full",
                                                    sorted ? "text-foreground" : "text-muted-foreground",
                                                    canSort && "hover:text-foreground"
                                                )}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {canSort ? (
                                                    <SortIcon
                                                        className={cn(
                                                            "w-3 h-3 transition-opacity shrink-0",
                                                            sorted ? "opacity-80" : "opacity-0 group-hover:opacity-40"
                                                        )}
                                                        aria-hidden
                                                    />
                                                ) : null}
                                            </span>
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={tableColumns.length} className="text-center">
                                    No forms found
                                </TableCell>
                            </TableRow>
                        ) : (
                            table.getRowModel().rows.map((row) => {
                                const formId = row.original.form_id;
                                return (
                                    <TableRow
                                        key={row.id}
                                        className={cn(
                                            formId && "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                                        )}
                                        onClick={() => {
                                            if (!formId) return;
                                            navigate({
                                                to: "/forms/$formId",
                                                params: { formId },
                                            });
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                className={cn(
                                                    cell.column.id === "form_name" && "w-[40%]",
                                                    cell.column.id === "actions" && "w-12 pr-2"
                                                )}
                                                onClick={
                                                    cell.column.id === "actions"
                                                        ? (event) => event.stopPropagation()
                                                        : undefined
                                                }
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}

export default FormsTable;
